import { KeyboardEvent, useEffect, useState } from "react";
import { ImageOff } from "lucide-react";

import {
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  Carousel as UICarousel,
} from "@/components/ui/carousel";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

const arrowClassName =
  "border-0 bg-black/50 text-white hover:bg-black/70 hover:text-white disabled:hidden";

export const Carousel = ({ imageUrls, className }: { imageUrls: string[]; className?: string }) => {
  const [current, setCurrent] = useState(0);
  const [openAt, setOpenAt] = useState<number | null>(null);
  const [thumbApi, setThumbApi] = useState<CarouselApi>();
  const [modalApi, setModalApi] = useState<CarouselApi>();

  useEffect(() => {
    if (!thumbApi) return;
    const onSelect = () => setCurrent(thumbApi.selectedScrollSnap());
    thumbApi.on("select", onSelect);
    return () => {
      thumbApi.off("select", onSelect);
    };
  }, [thumbApi]);

  useEffect(() => {
    if (!modalApi) return;
    const onSelect = () => thumbApi?.scrollTo(modalApi.selectedScrollSnap(), true);
    modalApi.on("select", onSelect);
    return () => {
      modalApi.off("select", onSelect);
    };
  }, [modalApi, thumbApi]);

  const onModalKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.defaultPrevented) return;
    if (event.key === "ArrowLeft") modalApi?.scrollPrev();
    if (event.key === "ArrowRight") modalApi?.scrollNext();
  };

  if (!imageUrls.length) {
    return (
      <div
        className={cn(
          "flex items-center justify-center rounded-lg bg-muted p-2 text-accent-foreground",
          className
        )}
      >
        <ImageOff className="size-8" />
      </div>
    );
  }

  const hasMany = imageUrls.length > 1;

  return (
    <div className={cn("overflow-hidden rounded-lg bg-muted", className)}>
      <UICarousel setApi={setThumbApi} className="h-full [&_[data-slot=carousel-content]]:h-full">
        <CarouselContent className="h-full">
          {imageUrls.map((url, index) => (
            <CarouselItem key={url} className="h-full">
              <img
                onClick={() => setOpenAt(index)}
                className="h-full w-full cursor-pointer object-cover"
                src={url}
                draggable={false}
                alt=""
              />
            </CarouselItem>
          ))}
        </CarouselContent>
        {hasMany && (
          <>
            <CarouselPrevious className={cn("left-2 size-7", arrowClassName)} />
            <CarouselNext className={cn("right-2 size-7", arrowClassName)} />
          </>
        )}
      </UICarousel>

      <Dialog open={openAt !== null} onOpenChange={open => !open && setOpenAt(null)}>
        <DialogContent className="p-2 sm:max-w-3xl" onKeyDown={onModalKeyDown}>
          <UICarousel setApi={setModalApi} opts={{ startIndex: openAt ?? 0 }}>
            <CarouselContent>
              {imageUrls.map(url => (
                <CarouselItem key={url} className="flex items-center justify-center">
                  <img
                    className="max-h-[80vh] w-full rounded-md object-contain"
                    src={url}
                    draggable={false}
                    alt=""
                  />
                </CarouselItem>
              ))}
            </CarouselContent>
            {hasMany && (
              <>
                <CarouselPrevious className={cn("left-2 size-9", arrowClassName)} />
                <CarouselNext className={cn("right-2 size-9", arrowClassName)} />
                <span className="-translate-x-1/2 absolute bottom-2 left-1/2 rounded-full bg-black/50 px-2 py-0.5 text-white text-xs tabular-nums">
                  {current + 1} / {imageUrls.length}
                </span>
              </>
            )}
          </UICarousel>
        </DialogContent>
      </Dialog>
    </div>
  );
};
